/** AP payments, aging, settings and reconciliation. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enBoApPayments = {
  // ---- Payments
  'bo.ap.pay.title': 'Supplier payments',
  'bo.ap.pay.subtitle': 'Payments to suppliers at {branch}, allocated to posted invoices',
  'bo.ap.pay.noBranch': 'Payments are recorded per branch.',
  'bo.ap.pay.add': 'Record payment',
  'bo.ap.pay.readOnly': 'You can view payments. Recording needs the "manage payables" permission.',
  'bo.ap.pay.readOnlyVerifier':
    'You verify or reject payments recorded by others. Recording needs the "manage payables" permission.',
  'bo.ap.pay.noPermission': 'Your role cannot view supplier payments at this branch.',
  'bo.ap.pay.listTitle': 'Payments',
  'bo.ap.pay.listDesc':
    'Newest first. A payment reduces the invoices only after another person verifies it.',
  'bo.ap.pay.filterLabel': 'Filter by status',
  'bo.ap.pay.empty': 'No payments yet — they appear here once finance records one.',
  'bo.ap.pay.emptyManage':
    'No payments yet — record a payment against a posted invoice with "Record payment".',
  'bo.ap.pay.emptyFiltered': 'No payments with this status.',
  'bo.ap.pay.col.number': 'Number',
  'bo.ap.pay.col.supplier': 'Supplier',
  'bo.ap.pay.col.channel': 'Channel',
  'bo.ap.pay.col.reference': 'Reference',
  'bo.ap.pay.col.date': 'Payment date',
  'bo.ap.pay.col.amount': 'Amount',
  'bo.ap.pay.col.status': 'Status',
  'bo.ap.pay.col.recordedBy': 'Recorded by',
  'bo.ap.pay.col.verifiedBy': 'Verified by',
  'bo.ap.pay.col.actions': 'Verification',
  'bo.ap.pay.allocationsOf': 'Allocations of {number}',
  'bo.ap.pay.rejectedBecause': 'Reason: {reason}',
  'bo.ap.pay.you': 'you',
  'bo.ap.pay.ownPayment': 'You recorded this payment — another verifier must decide.',
  'bo.ap.pay.sodNote':
    'Separation of duties: a payment is verified or rejected by someone other than the person who recorded it.',
  'bo.ap.pay.channel.bank_transfer': 'Bank transfer',
  'bo.ap.pay.channel.cash': 'Cash',
  'bo.ap.pay.channel.giro': 'Giro (cheque)',
  'bo.ap.pay.channel.other': 'Other',
  'bo.ap.pay.status.pending_verification': 'Awaiting verification',
  'bo.ap.pay.status.verified': 'Verified',
  'bo.ap.pay.status.rejected': 'Rejected',
  'bo.ap.pay.verify': 'Verify',
  'bo.ap.pay.verifying': 'Verifying…',
  'bo.ap.pay.reject': 'Reject',
  'bo.ap.pay.rejecting': 'Rejecting…',
  'bo.ap.pay.verifyTitle': 'Verify payment',
  'bo.ap.pay.verifyBody': '{number} to {supplier}:',
  'bo.ap.pay.verifyEffect':
    'Check the bank or cash evidence first. Verifying applies the allocations: the invoices’ outstanding amounts go down and cannot be changed afterwards.',
  'bo.ap.pay.rejectTitle': 'Reject payment',
  'bo.ap.pay.rejectEffect':
    'The allocations are released; the invoices stay open for a corrected payment.',
  'bo.ap.pay.rejectReason': 'Reason',
  'bo.ap.pay.rejectReasonHint': 'Shown to the person who recorded it, e.g. "Wrong transfer slip".',
  'bo.ap.pay.verified': 'Payment {number} verified.',
  'bo.ap.pay.rejected': 'Payment {number} rejected.',
  'bo.ap.pay.recorded': 'Payment {number} recorded — waiting for verification.',
  'bo.ap.pay.refusal.self': 'You recorded this payment; another person must verify or reject it.',
  'bo.ap.pay.refusal.permission': 'Your role cannot verify supplier payments.',
  'bo.ap.pay.createTitle': 'Record payment',
  'bo.ap.pay.createDesc': 'One payment to one supplier, spread over their open invoices.',
  // ---- Payment form
  'bo.ap.pay.form.explain':
    'Every rupiah of the payment is allocated to an invoice. The allocations must add up to the amount exactly.',
  'bo.ap.pay.form.supplier': 'Supplier',
  'bo.ap.pay.form.supplierPick': 'Choose a supplier',
  'bo.ap.pay.form.supplierHint': 'Their posted, unpaid invoices appear below.',
  'bo.ap.pay.form.supplierRequired': 'Choose the supplier you paid.',
  'bo.ap.pay.form.channel': 'Channel',
  'bo.ap.pay.form.reference': 'Reference',
  'bo.ap.pay.form.referenceHint': 'Transfer number or giro number, as on the evidence.',
  'bo.ap.pay.form.date': 'Payment date',
  'bo.ap.pay.form.dateHint': 'Business date in {timeZone}.',
  'bo.ap.pay.form.dateRequired': 'Enter the payment date.',
  'bo.ap.pay.form.amount': 'Amount ({currency})',
  'bo.ap.pay.form.amountHint': 'Digits only, in the smallest currency unit.',
  'bo.ap.pay.form.amountPreview': 'Amount:',
  'bo.ap.pay.form.amountRequired': 'Enter an amount above zero.',
  'bo.ap.pay.form.allocTitle': 'Allocation to invoices',
  'bo.ap.pay.form.allocDesc':
    'Remaining open = outstanding − allocations of other payments still awaiting verification.',
  'bo.ap.pay.form.autoAllocate': 'Allocate oldest due first',
  'bo.ap.pay.form.pickSupplierFirst': 'Choose a supplier to see their open invoices.',
  'bo.ap.pay.form.noOpenInvoices':
    'This supplier has no open invoices — only posted, unpaid invoices can be paid.',
  'bo.ap.pay.form.col.invoice': 'Invoice',
  'bo.ap.pay.form.col.due': 'Due date',
  'bo.ap.pay.form.col.outstanding': 'Outstanding',
  'bo.ap.pay.form.col.pending': 'Awaiting verification',
  'bo.ap.pay.form.col.remaining': 'Remaining open',
  'bo.ap.pay.form.col.allocation': 'Allocate',
  'bo.ap.pay.form.supplierInvoice': 'Supplier no. {number}',
  'bo.ap.pay.form.allocLabel': 'Allocation to {number}',
  'bo.ap.pay.form.fillFull': 'Fill in full',
  'bo.ap.pay.form.fillFullFor': 'Allocate the full remaining amount to {number}',
  'bo.ap.pay.form.fullyPending': 'Fully allocated to payments awaiting verification.',
  'bo.ap.pay.form.exceeds': 'More than the remaining open amount.',
  'bo.ap.pay.form.allocInvalid': 'Enter a whole amount.',
  'bo.ap.pay.form.overAllocated':
    'Another payment took part of this invoice meanwhile; {open} is still open.',
  'bo.ap.pay.form.notPayable': 'This invoice can no longer be paid. Reload and try again.',
  'bo.ap.pay.form.allocated': 'Allocated',
  'bo.ap.pay.form.amountTotal': 'Payment amount',
  'bo.ap.pay.form.difference': 'Difference',
  'bo.ap.pay.form.diffNoAmount': 'Enter the payment amount, then allocate it.',
  'bo.ap.pay.form.diffNone': 'Allocate the amount to at least one invoice.',
  'bo.ap.pay.form.diffBalanced': 'Allocations match the amount.',
  'bo.ap.pay.form.diffShort': '{amount} still to allocate.',
  'bo.ap.pay.form.diffOver': 'Allocations exceed the amount by {amount}.',
  'bo.ap.pay.form.serverMismatch': 'The allocations must add up to the payment amount.',
  'bo.ap.pay.form.sodNote':
    'After saving, the payment waits for verification by another person; the invoices are not reduced until then.',
  'bo.ap.pay.form.submit': 'Record payment',
  'bo.ap.pay.form.submitting': 'Recording…',
  // ---- Aging
  'bo.ap.aging.title': 'Payables aging',
  'bo.ap.aging.subtitle': 'Open supplier invoices at {branch} by days overdue',
  'bo.ap.aging.noBranch': 'Aging is reported per branch.',
  'bo.ap.aging.noPermission': 'Your role cannot view payables at this branch.',
  'bo.ap.aging.asOfTitle': 'Report date',
  'bo.ap.aging.asOfDesc': 'Balances as they stood at the end of this day.',
  'bo.ap.aging.asOf': 'As of',
  'bo.ap.aging.today': 'Today',
  'bo.ap.aging.listTitle': 'Open payables on {date}',
  'bo.ap.aging.listDesc':
    'Per supplier, largest first. Dates in {timeZone}. Open a row for its invoices.',
  'bo.ap.aging.empty': 'Nothing open on {date} — posted invoices with a balance appear here.',
  'bo.ap.aging.col.supplier': 'Supplier',
  'bo.ap.aging.col.total': 'Total',
  'bo.ap.aging.col.invoice': 'Invoice',
  'bo.ap.aging.col.due': 'Due date',
  'bo.ap.aging.col.daysOverdue': 'Days overdue',
  'bo.ap.aging.col.bucket': 'Bucket',
  'bo.ap.aging.col.open': 'Open',
  'bo.ap.aging.bucket.not_due': 'Not yet due',
  'bo.ap.aging.bucket.d0_30': '1–30 days',
  'bo.ap.aging.bucket.d31_60': '31–60 days',
  'bo.ap.aging.bucket.d61_90': '61–90 days',
  'bo.ap.aging.bucket.d90_plus': '>90 days',
  'bo.ap.aging.totals': 'Total',
  'bo.ap.aging.expand': 'show invoices',
  'bo.ap.aging.collapse': 'hide invoices',
  'bo.ap.aging.invoiceCount': '{count} open invoice(s)',
  'bo.ap.aging.days': '{days} days',
  'bo.ap.aging.csv': 'Export CSV',
  'bo.ap.aging.definitionsTitle': 'Definitions',
  'bo.ap.aging.definitionsDesc': 'How the server calculated this report.',
  'bo.ap.aging.definitionsNone': 'No definitions were returned.',
  'bo.ap.aging.def.scope': 'Scope',
  'bo.ap.aging.def.open': 'Open amount',
  'bo.ap.aging.def.buckets': 'Buckets',
  // ---- Settings
  'bo.ap.settings.title': 'Matching settings',
  'bo.ap.settings.subtitle':
    'Tolerances for matching supplier invoices to purchase orders, for every branch',
  'bo.ap.settings.readOnly':
    'Read only — changing the matching settings needs the "approve payables" permission.',
  'bo.ap.settings.cardTitle': 'Invoice matching',
  'bo.ap.settings.cardDesc':
    'Differences within these tolerances still match; larger ones become exceptions to resolve.',
  'bo.ap.settings.price': 'Price tolerance',
  'bo.ap.settings.priceHint': 'Invoice unit price vs the PO price.',
  'bo.ap.settings.qty': 'Quantity tolerance',
  'bo.ap.settings.qtyHint': 'Invoiced quantity vs ordered or received.',
  'bo.ap.settings.bpsHint': '{hint} Stored as {bps} bps.',
  'bo.ap.settings.percentInvalid': 'Enter a percentage from 0 to 100, at most 2 decimals.',
  'bo.ap.settings.amount': 'Amount tolerance ({currency})',
  'bo.ap.settings.amountHint': 'Per invoice line, in the smallest currency unit.',
  'bo.ap.settings.amountPreview': 'Up to',
  'bo.ap.settings.amountZero': 'No amount difference is accepted.',
  'bo.ap.settings.amountInvalid': 'Enter a whole amount (0 or more).',
  'bo.ap.settings.mode': 'Default match for invoices with a purchase order',
  'bo.ap.settings.twoWay': '2-way match',
  'bo.ap.settings.twoWayHint':
    'Invoice vs purchase order: prices and ordered quantities. For services or goods without a receipt.',
  'bo.ap.settings.threeWay': '3-way match',
  'bo.ap.settings.threeWayHint':
    'Invoice vs purchase order vs goods receipt: you only pay for what was actually received. Recommended for stock.',
  'bo.ap.settings.appliesNote':
    'Applies to matches run from now on; invoices already matched keep their result.',
  'bo.ap.settings.submit': 'Save settings',
  'bo.ap.settings.saved': 'Matching settings saved.',
  'bo.ap.settings.conflict':
    'Someone else changed these settings meanwhile. The latest values are loaded — check them and save again.',
  // ---- Reconciliation
  'bo.recon.title': 'Reconciliation',
  'bo.recon.subtitle': 'Stock and payables projections at {branch} checked against their ledgers',
  'bo.recon.noBranch': 'Reconciliation runs per branch.',
  'bo.recon.noPermission':
    'Reconciliation needs the stock approval, audit or payables approval permission.',
  'bo.recon.refresh': 'Check again',
  'bo.recon.checking': 'Checking…',
  'bo.recon.statusTitle': 'Result',
  'bo.recon.checkedAt': 'Checked {time}',
  'bo.recon.ok': 'Reconciled',
  'bo.recon.okText': 'Every projection matches its ledger.',
  'bo.recon.issueBadge': '{count} difference(s)',
  'bo.recon.issuesText':
    'A difference indicates a defect to investigate — never adjust the ledger to make it match.',
  'bo.recon.backlog': 'Stock consumption backlog',
  'bo.recon.backlogCount': '{count} order item(s)',
  'bo.recon.backlogPending':
    'Accepted order items whose ingredients are not yet deducted from stock. Stock figures catch up once they are processed.',
  'bo.recon.backlogNone': 'All accepted order items have been deducted from stock.',
  'bo.recon.checksTitle': 'Checks',
  'bo.recon.checksDesc': 'Each projection recomputed from its append-only source.',
  'bo.recon.issuesTitle': 'Differences',
  'bo.recon.issuesDesc': 'Expected = from the ledger; actual = the stored projection.',
  'bo.recon.noIssues': 'No differences — nothing to investigate.',
  'bo.recon.unitsNote':
    'Values are raw: quantities in thousandths of the base unit, money in the smallest currency unit.',
  'bo.recon.col.check': 'Check',
  'bo.recon.col.checked': 'Checked',
  'bo.recon.col.issues': 'Differences',
  'bo.recon.col.result': 'Result',
  'bo.recon.col.entity': 'Entity',
  'bo.recon.col.expected': 'Expected',
  'bo.recon.col.actual': 'Actual',
  'bo.recon.col.detail': 'Detail',
  'bo.recon.check.cost_pool_vs_ledger': 'Cost pool vs stock ledger',
  'bo.recon.check.location_balance_vs_ledger': 'Location balance vs stock ledger',
  'bo.recon.check.lot_balance_vs_ledger': 'Lot balance vs stock ledger',
  'bo.recon.check.po_received_vs_receipts': 'PO received vs goods receipts',
  'bo.recon.check.receipt_vs_movements': 'Goods receipt vs stock movements',
  'bo.recon.check.ap_status_vs_events': 'Invoice status vs AP events',
  'bo.recon.check.ap_negative_balance': 'Negative payable balance',
  'bo.recon.hint.cost_pool_vs_ledger': 'Quantity and value per item.',
  'bo.recon.hint.location_balance_vs_ledger': 'Quantity per item and location.',
  'bo.recon.hint.lot_balance_vs_ledger': 'Quantity per lot.',
  'bo.recon.hint.po_received_vs_receipts': 'Received quantity per PO line.',
  'bo.recon.hint.receipt_vs_movements': 'Accepted quantity posted to stock per receipt line.',
  'bo.recon.hint.ap_status_vs_events': 'Paid / partially paid status per invoice.',
  'bo.recon.hint.ap_negative_balance': 'Open balance per invoice never below zero.',
  'bo.recon.detail.quantity': 'Quantity',
  'bo.recon.detail.value': 'Value',
  'bo.recon.detail.acceptedQty': 'Accepted quantity (PO unit)',
  'bo.recon.detail.baseQty': 'Base quantity posted',
  'bo.recon.detail.negativeOpen': 'Open balance below zero',
} as const;
